import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

configure({ enforceActions: 'always' });

class CalendarAvailabilityStore extends LocalModel {
    constructor() {
        super('calendarAvailability', {
            namePlural: 'calendarAvailabilities',
            namePluralReal: 'calendarAvailabilities',
            sort: '-createdDate',
            limit: 10000,
            api: {
                search: {
                    url: '/api/calendaravailabilities/',
                    params: {
                        limit: 10000,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/calendaravailabilities/',
                    params: {},
                },
                save: {
                    url: '/api/calendaravailabilities/',
                    params: {},
                },
                delete: {
                    url: '/api/calendaravailabilities/',
                    params: {},
                },
            },
        });
    }

    @observable newCalendarAvailability = {};

    @observable calendarAvailability = {};

    @observable calendarAvailabilities = [];

    @observable calendars = [];

    @observable animals = [];

    @observable visitors = [];

    @observable clientHours = [];

    @observable adminAnimals = [];

    @observable totalHours = [];

    @observable animalBookings = [];

    // Override LocalModel.insertMany, which only returns the response for
    // status 202 and swallows everything else (the backend answers 201 on
    // success and 400 on invalid dates). Returning the full response lets
    // callers show an error instead of silently doing nothing.
    async insertMany(data) {
        const response = await util.fetchApi(
            '/api/calendaravailabilities/multiple',
            { publish: false, method: 'POST' },
            { data },
        );
        return response;
    }

    getEvents(date, calendar) {
        const { calendarAvailabilities } = this;
        const start = new Date(date);
        const end = new Date(date);
        // Set end to 59 minutes and 59 seconds
        end.setMinutes(end.getMinutes() + 59);
        end.setSeconds(end.getSeconds() + 59);

        const events = calendarAvailabilities
            .filter(e => calendar === e.calendar)
            .filter(e => {
                const eventStart = new Date(e.start);
                // const eventEnd = new Date(e.end);
                return eventStart >= start && eventStart <= end;
            });
        return events;
    }

    getEventsForCalendar(date, calendar, category = null) {
        const { calendarAvailabilities } = this;
        const start = new Date(date);
        const end = new Date(date);
        // Set end to 59 minutes and 59 seconds
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        const events = calendarAvailabilities
            .filter(e => calendar === e.calendar)
            .filter(e => {
                // Category filter: null = all, 'normal' = normal/empty, 'onlineBooking' = online booking only
                if (category === null) return true;
                if (category === 'normal') return !e.category || e.category === 'normal';
                return e.category === category;
            })
            .filter(e => {
                const eventStart = new Date(e.start);
                // const eventEnd = new Date(e.end);
                return eventStart >= start && eventStart <= end;
            });
        return events;
    }

    getAvailableHours(date, calendar, category = null) {
        const { calendarAvailabilities } = this;
        const start = new Date(date);
        const end = new Date(date);
        // Set end to 59 minutes and 59 seconds
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        const events = calendarAvailabilities
            .filter(e => calendar === e.calendar)
            .filter(e => {
                // Category filter: null = all, 'normal' = normal/empty, 'onlineBooking' = online booking only
                if (category === null) return true;
                if (category === 'normal') return !e.category || e.category === 'normal';
                return e.category === category;
            })
            .filter(e => {
                const eventStart = new Date(e.start);
                // const eventEnd = new Date(e.end);
                return eventStart >= start && eventStart <= end;
            });

        let startHour = null;
        let endHour = null;
        events.forEach(e => {
            const eventStart = new Date(e.start);
            const eventEnd = new Date(e.end);
            if (startHour === null || eventStart < startHour) {
                startHour = eventStart;
            }
            if (endHour === null || eventEnd > endHour) {
                endHour = eventEnd;
            }
        });
        return {
            startHour: startHour ? startHour.getHours() : null,
            startMinute: startHour ? startHour.getMinutes() : null,
            endHour: endHour ? endHour.getHours() : null,
            endMinute: endHour ? endHour.getMinutes() : null,
        };
    }

    /**
     * Get available hours with priority.
     * Normal hours drive white/gray background.
     * Online booking hours are tracked separately for striped overlay.
     */
    getAvailableHoursWithPriority(date, calendar) {
        const normalHours = this.getAvailableHours(date, calendar, 'normal');
        const onlineHours = this.getAvailableHours(date, calendar, 'onlineBooking');

        const hasNormal = normalHours.startHour !== null && normalHours.endHour !== null;
        const hasOnline = onlineHours.startHour !== null && onlineHours.endHour !== null;

        if (hasNormal) {
            // Normal hours drive white/gray background; online booking tracked separately
            return {
                ...normalHours,
                isOnlineBooking: false,
                hasOnlineBooking: hasOnline,
                onlineStartHour: hasOnline ? onlineHours.startHour : null,
                onlineStartMinute: hasOnline ? onlineHours.startMinute : null,
                onlineEndHour: hasOnline ? onlineHours.endHour : null,
                onlineEndMinute: hasOnline ? onlineHours.endMinute : null,
            };
        }
        if (hasOnline) {
            // Only online booking: it drives white/gray AND gets stripes
            return {
                ...onlineHours,
                isOnlineBooking: true,
                hasOnlineBooking: true,
                onlineStartHour: onlineHours.startHour,
                onlineStartMinute: onlineHours.startMinute,
                onlineEndHour: onlineHours.endHour,
                onlineEndMinute: onlineHours.endMinute,
            };
        }
        // Neither: no availability
        return {
            ...normalHours,
            isOnlineBooking: false,
            hasOnlineBooking: false,
            onlineStartHour: null,
            onlineStartMinute: null,
            onlineEndHour: null,
            onlineEndMinute: null,
        };
    }

    /**
     * Does the calendar have an active turnus on this day?
     * True when ANY turnus row (normal rotation or online booking) starts on
     * the given day - the same condition under which the day-view column
     * header prints "(08:00-16:00)". Drives the calendar toolbar toggle
     * "show only calendars with active turnus today".
     * Compares against null on purpose: a turnus starting at 00:00 is hour 0.
     */
    hasAvailability(date, calendar) {
        const { startHour, endHour } = this.getAvailableHoursWithPriority(date, calendar);
        return startHour !== null && endHour !== null;
    }

    @action
    isOverlapping() {
        const { calendarAvailabilities } = this;
        calendarAvailabilities.forEach(e => {
            e.overlapping = false;
            const start = new Date(e.start);
            const end = new Date(e.end);
            const events = calendarAvailabilities
                .filter(e2 => e.calendar === e2.calendar)
                .filter(e2 => e2.id !== e.id)
                .filter(e2 => {
                    // Find overlapping events for this event
                    const eventStart = new Date(e2.start);
                    const eventEnd = new Date(e2.end);
                    const isOverlapping = (start >= eventStart && start <= eventEnd) || (end >= eventStart && end <= eventEnd);
                    return isOverlapping;
                });
            if (events.length > 0) {
                e.overlapping = true;
            }
        });
    }
}

const store = new CalendarAvailabilityStore();
export default store;
